import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import type { AccountSource, LineAccountRule } from '../../types/pettyCash';

/**
 * Where a receipt's G/L account comes from: an SAP Advanced G/L rule (as for a purchase through PR -> PO ->
 * receipt), finance's choice for this receipt, or the category's fixed account.
 */

/** "Rule 03 Normal WH for EQU · class Utilities, warehouse 3ESK1001 (Normal) · cost account (consumed at once)" */
export function ruleSummary(rule: LineAccountRule): string {
  const where = rule.warehouseCode
    ? `warehouse ${rule.warehouseCode}${rule.warehouseType ? ` (${rule.warehouseType})` : ''}`
    : rule.warehouseType ? `warehouse type ${rule.warehouseType}` : '';
  const what = rule.itemCode ? `item ${rule.itemCode}` : rule.itemClass ? `class ${rule.itemClass}` : '';
  const kind = rule.issued ? 'cost account (consumed at once)' : 'stays on the inventory (clearing) account';
  return [`Rule ${rule.code}${rule.description ? ` ${rule.description}` : ''}`, [what, where].filter(Boolean).join(', '), kind]
    .filter(Boolean)
    .join(' · ');
}

const AccountSourceChip: React.FC<{ source: AccountSource | null | undefined; rule?: LineAccountRule | null }> = ({ source, rule }) => {
  if (!source) return null;
  if (source === 'rule') {
    return (
      <Tooltip title={rule ? ruleSummary(rule) : "SAP's Advanced G/L rules"}>
        <Chip size="small" variant="outlined" color="info" label={rule?.code ? `SAP rule ${rule.code}` : 'SAP rule'} sx={{ mt: 0.5 }} />
      </Tooltip>
    );
  }
  if (source === 'line') {
    return (
      <Tooltip title="Set by finance for this receipt">
        <Chip size="small" variant="outlined" color="warning" label="Finance" sx={{ mt: 0.5 }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title="The category's fixed account">
      <Chip size="small" variant="outlined" label="Category" sx={{ mt: 0.5 }} />
    </Tooltip>
  );
};

export default AccountSourceChip;
